import React, { useEffect, useState } from 'react';
import { useGetUserDataAndSetInLocalStorage } from '@/hooks/useGetUserDataAndSetInLocalStorage';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GPC_TOAST, SESSION_FLAG } from '../../constants';
import { isGpcApplicable } from '../../helpers';
import styles from './GpcToast.module.scss';

/**
 * Subtle, non-blocking confirmation that the GPC opt-out was honored.
 * Shown once per session (sessionStorage flag), auto-hides after ~5s.
 */
export const GpcToast: React.FC = () => {
	const { t } = useTranslationWithLocale('common');
	// Shares the 'user data' react-query cache with the cookie providers, so
	// this neither duplicates the request nor leaks before geo resolves.
	const { isLoading: userDataIsLoading } = useGetUserDataAndSetInLocalStorage();

	// Decide synchronously when geo is already in localStorage (return visit).
	// On first visit geo resolves asynchronously, so this starts false and the
	// effect below promotes it once the lookup finishes.
	const [visible, setVisible] = useState(() => {
		if (typeof window === 'undefined') return false;
		if (sessionStorage.getItem(SESSION_FLAG)) return false;
		return isGpcApplicable();
	});

	// Re-evaluate once the async geo lookup completes (first visit).
	useEffect(() => {
		if (visible || userDataIsLoading) return;
		if (typeof window === 'undefined') return;
		if (sessionStorage.getItem(SESSION_FLAG)) return;
		if (!isGpcApplicable()) return;

		setVisible(true);
	}, [userDataIsLoading, visible]);

	// Auto-hide tied to `visible` (not a one-shot mount effect) so React
	// StrictMode's mount→cleanup→mount cycle re-arms the timer instead of
	// clearing it permanently.
	useEffect(() => {
		if (!visible) return;

		sessionStorage.setItem(SESSION_FLAG, '1');

		const timer = setTimeout(() => setVisible(false), GPC_TOAST.AUTO_HIDE_MS);
		return () => clearTimeout(timer);
	}, [visible]);

	if (!visible) return null;

	return (
		<div className={styles['toast']} role='status' aria-live='polite'>
			{t('gpc_toastText')}
		</div>
	);
};
